import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { documentsAPI, quotationsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import DocumentTemplate from '../../components/DocumentTemplate';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const QuotationPrintPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateSettings, setTemplateSettings] = useState({});

  useEffect(() => {
    loadPrintData();
    loadTemplateSettings();
  }, [id]);

  const loadPrintData = async () => {
    try {
      setLoading(true);
      const response = await documentsAPI.getPrintData(id);
      setData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load print data'));
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateSettings = async () => {
    try {
      const response = await api.get('/templates/quotation');
      setTemplateSettings(response.data);
    } catch (e) {
      // Fallback to localStorage on template-settings fetch failure --
      // the settings endpoint is non-critical; we'd rather render with
      // defaults than block the preview.
      const savedSettings = localStorage.getItem('documentTemplateSettings');
      if (savedSettings) {
        try {
          setTemplateSettings(JSON.parse(savedSettings));
        } catch (parseError) {
          // Swallow -- template settings aren't user-facing feedback-worthy.
        }
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Server-side PDF generation via the backend /pdf endpoint (added
  // 2026-04-16) -- was previously a window.print() stub with a toast
  // telling the user to use "Save as PDF" from the print dialog. Now
  // a real PDF stream comes back and is downloaded directly.
  const handleDownloadPDF = async () => {
    try {
      const response = await quotationsAPI.downloadPdf(id);
      const url = window.URL.createObjectURL(response.data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `Quotation_${data?.document?.doc_no || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to generate PDF'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Document not found</p>
          <Button 
            onClick={() => {
              const businessPath = activeBusiness?.id ? `/business/${activeBusiness.id}` : '';
              navigate(`${businessPath}/sales/quotations`);
            }}
            className="mt-4"
          >
            Back to Quotations
          </Button>
        </div>
      </div>
    );
  }

  const { document, business } = data;

  return (
    <div className="bg-gray-100 print:bg-white min-h-screen">
      {/* Action Bar - Hidden on print */}
      <div className="print:hidden bg-white border-b-2 border-blue-100 px-6 py-4 sticky top-0 z-20 shadow-md">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <button
            onClick={() => {
              const businessPath = activeBusiness?.id ? `/business/${activeBusiness.id}` : '';
              navigate(`${businessPath}/sales/quotations`);
            }}
            className="flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 group-hover:text-blue-600" />
            <span className="font-medium text-gray-700 group-hover:text-blue-600">Back to Quotations</span>
          </button>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleDownloadPDF}
              className="hover:bg-blue-50 border-blue-300"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button 
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Print Preview */}
      <div className="py-8 print:py-0">
        <div className="max-w-[210mm] mx-auto bg-white shadow-2xl print:shadow-none">
          <DocumentTemplate 
            documentType="QUOTATION"
            document={document}
            business={business}
            templateSettings={templateSettings}
          />
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }

          body {
            margin: 0;
            padding: 0;
          }

          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          table {
            page-break-inside: auto;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          thead {
            display: table-header-group;
          }

          .no-break {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default QuotationPrintPreview;
